import React, { Suspense, lazy, useEffect } from 'react';
import { createBrowserRouter, Navigate, useParams, useRouteError } from 'react-router-dom';
import { tokens } from './lib/styleTokens';
import { useAuth } from './hooks/useAuth';
import ProtectedLayout from './components/ProtectedLayout';

const publicLoader = (
  <div
    style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'var(--surface-primary)',
      color: 'var(--text-secondary)',
    }}
  >
    Loading…
  </div>
);

function withSuspense(Component: React.ComponentType) {
  return function SuspenseWrapper(props: Record<string, unknown>) {
    return (
      <Suspense fallback={publicLoader}>
        <Component {...props} />
      </Suspense>
    );
  };
}

const LandingPage = withSuspense(lazy(() => import('./pages/LandingPage')));
const Login = withSuspense(lazy(() => import('./pages/Login')));
const RequestPasswordReset = withSuspense(lazy(() => import('./pages/RequestPasswordReset')));
const ResetPassword = withSuspense(lazy(() => import('./pages/ResetPassword')));
const Join = withSuspense(lazy(() => import('./pages/Join')));
const Onboarding = withSuspense(lazy(() => import('./pages/Onboarding')));
const SharedWorkflow = withSuspense(lazy(() => import('./pages/SharedWorkflow')));
const Respond = withSuspense(lazy(() => import('./pages/Respond')));
const WidgetEmbed = withSuspense(lazy(() => import('./pages/WidgetEmbed')));

const Dashboard = lazy(() => import('./pages/Dashboard'));
const CommandCentre = lazy(() => import('./pages/CommandCentre'));
const Checkout = lazy(() => import('./pages/Checkout'));
const FinanceOverview = lazy(() => import('./pages/FinanceOverview'));
const Transactions = lazy(() => import('./pages/Transactions'));
const PastTransactions = lazy(() => import('./pages/PastTransactions'));
const Accounts = lazy(() => import('./pages/Accounts'));
const Invoices = lazy(() => import('./pages/Invoices'));
const Orders = lazy(() => import('./pages/Orders'));
const Catalog = lazy(() => import('./pages/Catalog'));
const Forms = lazy(() => import('./pages/Forms'));
const Inputs = lazy(() => import('./pages/Inputs'));
const InputsBuilder = lazy(() => import('./pages/InputsBuilder'));
const LandingPages = lazy(() => import('./pages/LandingPages'));
const MediaLibrary = lazy(() => import('./pages/MediaLibrary'));
const Storefront = lazy(() => import('./pages/Storefront'));
const Contacts = lazy(() => import('./pages/Contacts'));
const ContactDetail = lazy(() => import('./pages/ContactDetail'));
const Conversations = lazy(() => import('./pages/Conversations'));
const ConversationDetail = lazy(() => import('./pages/ConversationDetail'));
const Integrations = lazy(() => import('./pages/Integrations'));
const Workflows = lazy(() => import('./pages/Workflows'));
const WorkflowEditor = lazy(() => import('./pages/WorkflowEditor'));
const WorkflowRuns = lazy(() => import('./pages/WorkflowRuns'));
const WorkflowRunDetail = lazy(() => import('./pages/WorkflowRunDetail'));
const Templates = lazy(() => import('./pages/Templates'));
const Inventory = lazy(() => import('./pages/Inventory'));
const Assets = lazy(() => import('./pages/Assets'));
const Actors = lazy(() => import('./pages/Actors'));
const Capacity = lazy(() => import('./pages/Capacity'));
const CapacityTopup = lazy(() => import('./pages/CapacityTopup'));
const CapacityAnalytics = lazy(() => import('./pages/CapacityAnalytics'));
const Settings = lazy(() => import('./pages/Settings'));
const Branding = lazy(() => import('./pages/Branding'));
const Branches = lazy(() => import('./pages/Branches'));
const Roles = lazy(() => import('./pages/Roles'));
const Team = lazy(() => import('./pages/Team'));
const Approvals = lazy(() => import('./pages/Approvals'));
const ApprovalDetail = lazy(() => import('./pages/ApprovalDetail'));
const Audit = lazy(() => import('./pages/Audit'));
const MerchantDashboard = lazy(() => import('./pages/MerchantDashboard'));
const MerchantProducts = lazy(() => import('./pages/MerchantProducts'));
const Assistant = lazy(() => import('./pages/Assistant'));
const AiAssistants = lazy(() => import('./pages/AiAssistants'));
const AiAssistantEdit = lazy(() => import('./pages/AiAssistantEdit'));
const AssistantInsights = lazy(() => import('./pages/AssistantInsights'));
const AiCredits = lazy(() => import('./pages/AiCredits'));
const Widgets = lazy(() => import('./pages/Widgets'));
const FormAnalytics = lazy(() => import('./pages/FormAnalytics'));
const Views = lazy(() => import('./pages/Views'));
const Timeline = lazy(() => import('./pages/Timeline'));
const ApiTokens = lazy(() => import('./pages/ApiTokens'));
const Invitations = lazy(() => import('./pages/Invitations'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const AdminAnalytics = lazy(() => import('./pages/AdminAnalytics'));
const AdminMemoryTracker = lazy(() => import('./pages/AdminMemoryTracker'));
const AdminDoc = lazy(() => import('./pages/AdminDoc'));

function HomeRoute() {
  const { user } = useAuth();
  return user ? <Navigate to="/dashboard" replace /> : <LandingPage />;
}

function LoginRoute() {
  const { user } = useAuth();
  return user ? <Navigate to="/dashboard" replace /> : <Login />;
}

function OnboardingRoute() {
  const { user } = useAuth();
  return user ? <Onboarding /> : <Navigate to="/login" replace />;
}

const publicRoutes = [
  { path: '/', element: <HomeRoute /> },
  { path: '/login', element: <LoginRoute /> },
  { path: '/forgot-password', element: <RequestPasswordReset /> },
  { path: '/reset-password', element: <ResetPassword /> },
  { path: '/join', element: <Join /> },
  { path: '/onboarding', element: <OnboardingRoute /> },
  { path: '/shared/workflow/:token', element: <SharedWorkflow /> },
  { path: '/respond/:request_id', element: <Respond /> },
  { path: '/widget-embed/:assistantId?', element: <WidgetEmbed /> },
];

function PagesBuildRedirect() {
  const { id } = useParams();
  return <Navigate to={`/pages?edit=${id}`} replace />;
}

function PagesNewRedirect() {
  return <Navigate to="/pages?new=1" replace />;
}

function InputsNewRedirect() {
  return <Navigate to="/inputs?new=1" replace />;
}

function RootErrorBoundary() {
  const error = useRouteError();
  const message = error instanceof Error ? error.message : String(error ?? 'Unknown error');

  useEffect(() => {
    if (message.includes('dynamically imported module') || message.includes('Failed to fetch dynamically')) {
      // A new deployment removed the old JS chunk the SPA was trying to load.
      // Hard-reload so the browser fetches the fresh index.html and current assets.
      window.location.reload();
    }
  }, [message]);

  if (message.includes('dynamically imported module') || message.includes('Failed to fetch dynamically')) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <div style={{ textAlign: 'center', maxWidth: 400 }}>
          <h1 style={{ fontSize: 18, marginBottom: 8 }}>Updating app…</h1>
          <p style={{ fontSize: 14, color: tokens.muted, marginBottom: 16 }}>
            A new version is being loaded.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ textAlign: 'center', maxWidth: 480 }}>
        <h1 style={{ fontSize: 18, marginBottom: 8 }}>Unexpected error</h1>
        <p style={{ fontSize: 14, color: tokens.muted, marginBottom: 16, wordBreak: 'break-word' }}>{message}</p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          style={{ padding: '8px 16px', fontSize: 14, background: tokens.ctaBg, color: tokens.ctaText, border: 'none', borderRadius: 6, cursor: 'pointer' }}
        >
          Reload page
        </button>
      </div>
    </div>
  );
}

const protectedRoutes = [
  { path: 'dashboard', element: <Dashboard /> },
  { path: 'command-centre', element: <CommandCentre /> },
  { path: 'checkout', element: <Checkout /> },
  { path: 'finance', element: <FinanceOverview /> },
  { path: 'transactions', element: <Transactions /> },
  { path: 'record-transaction', element: <PastTransactions /> },
  { path: 'accounts', element: <Accounts /> },
  { path: 'invoices', element: <Invoices /> },
  { path: 'orders', element: <Orders /> },
  { path: 'catalog', element: <Catalog /> },
  { path: 'merchant-products', element: <MerchantProducts /> },
  { path: 'offerings', element: <MerchantProducts /> },
  { path: 'forms', element: <Forms /> },
  { path: 'inputs', element: <Inputs /> },
  { path: 'inputs/new', element: <InputsNewRedirect /> },
  { path: 'inputs/:id/build', element: <InputsBuilder /> },
  { path: 'pages', element: <LandingPages /> },
  { path: 'pages/new', element: <PagesNewRedirect /> },
  { path: 'pages/:id/build', element: <PagesBuildRedirect /> },
  { path: 'media-library', element: <MediaLibrary /> },
  { path: 'storefront', element: <Storefront /> },
  { path: 'contacts', element: <Contacts /> },
      { path: 'contacts/:id', element: <ContactDetail /> },
  { path: 'conversations', element: <Conversations /> },
  { path: 'conversations/:id', element: <ConversationDetail /> },
  { path: 'integrations', element: <Integrations /> },
  { path: 'workflows', element: <Workflows /> },
  { path: 'workflows/new', element: <WorkflowEditor /> },
  { path: 'workflows/:id/edit', element: <WorkflowEditor /> },
  { path: 'workflows/:workflow_id/runs', element: <WorkflowRuns /> },
  { path: 'workflows/:workflow_id/runs/:run_id', element: <WorkflowRunDetail /> },
  { path: 'templates', element: <Templates /> },
  { path: 'inventory', element: <Inventory /> },
  { path: 'assets', element: <Assets /> },
  { path: 'actors', element: <Actors /> },
  { path: 'branches', element: <Branches /> },
  { path: 'capacity', element: <Capacity /> },
  { path: 'capacity/topup', element: <CapacityTopup /> },
  { path: 'capacity/analytics', element: <CapacityAnalytics /> },
  { path: 'settings', element: <Settings /> },
  { path: 'settings/branding', element: <Branding /> },
  { path: 'settings/branches', element: <Navigate to="/branches" replace /> },
  { path: 'settings/roles', element: <Roles /> },
  { path: 'team', element: <Team /> },
  { path: 'approvals', element: <Approvals /> },
  { path: 'approvals/:id', element: <ApprovalDetail /> },
  { path: 'audit', element: <Audit /> },
  { path: 'merchant', element: <MerchantDashboard /> },
  { path: 'merchant/products', element: <MerchantProducts /> },
  { path: 'assistant', element: <Assistant /> },
  { path: 'ai-assistants', element: <AiAssistants /> },
  { path: 'ai-assistants/:id/edit', element: <AiAssistantEdit /> },
  { path: 'ai-assistants/:id/insights', element: <AssistantInsights /> },
  { path: 'ai-credits', element: <AiCredits /> },
  { path: 'widgets', element: <Widgets /> },
  { path: 'form-analytics', element: <FormAnalytics /> },
  { path: 'views', element: <Views /> },
  { path: 'timeline', element: <Timeline /> },
  { path: 'api-tokens', element: <ApiTokens /> },
  { path: 'invitations', element: <Invitations /> },
  { path: 'admin', element: <AdminDashboard /> },
  { path: 'admin/analytics', element: <AdminAnalytics /> },
  { path: 'admin/memory-tracker', element: <AdminMemoryTracker /> },
  { path: 'admin/doc', element: <AdminDoc /> },
];

export const mainRouter = createBrowserRouter([
  ...publicRoutes,
  {
    element: <ProtectedLayout />,
    errorElement: <RootErrorBoundary />,
    children: protectedRoutes,
  },
  { path: '*', element: <div style={{ padding: 40 }}>Page not found</div>, errorElement: <RootErrorBoundary /> },
]);

export const authRouter = createBrowserRouter([
  { path: '/', element: <Login /> },
  { path: '/login', element: <Login /> },
  { path: '/forgot-password', element: <RequestPasswordReset /> },
  { path: '/reset-password', element: <ResetPassword /> },
  { path: '/join', element: <Join /> },
  { path: '*', element: <Navigate to="/" replace /> },
], { basename: '/' });
